<script lang="ts">
	import type { SbImage } from '../types';
	import SbRichtext from '../components/sbRichtext.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import { innerWidth } from 'svelte/reactivity/window';

	interface Props {
		content: ISbStoryData<{
			headline: string | ISbRichtext;
			logos: SbImage[];
			duration: number;
			size: 'small' | 'medium' | 'large';
			desktop_size: 'small' | 'medium' | 'large';
		}>['content'];
	}

	let { content }: Props = $props();

	let logosWidth = $state(0);
	let playAsMarquee = $derived(logosWidth > (innerWidth.current || 0));

	const sizeClasses = $derived({
		'h-5': !content.size || content.size === 'small',
		'h-7': content.size === 'medium',
		'h-9': content.size === 'large',
		'lg:h-5': !content.desktop_size || content.desktop_size === 'small',
		'lg:h-7': content.desktop_size === 'medium',
		'lg:h-9': content.desktop_size === 'large'
	});
</script>

<section class="relative my-10 overflow-x-hidden" use:editable={content}>
	{#if content.headline}
		<h1
			class="mb-5 text-balance text-center text-[14px]/[140%] font-semibold uppercase tracking-[1.12px] text-brown-4"
		>
			<SbRichtext text={content.headline} />
		</h1>
	{/if}

	<div
		class={['mx-auto flex items-center gap-x-8', playAsMarquee && 'animate-marquee pl-8']}
		style="width: max-content; --duration: {content.duration || 20}s;"
	>
		<div class="flex items-center gap-x-8" bind:clientWidth={logosWidth}>
			{#each content.logos as logo}
				<img
					loading="lazy"
					class={['flex-none select-none', sizeClasses]}
					src={logo.filename}
					alt={logo.alt ?? ''}
					title={logo.title ?? ''}
				/>
			{/each}
		</div>
		{#if playAsMarquee}
			<div class="flex items-center gap-x-8">
				{#each content.logos as logo}
					<img
						aria-hidden="true"
						loading="lazy"
						class={['flex-none select-none', sizeClasses]}
						src={logo.filename}
						alt={logo.alt ?? ''}
						title={logo.title ?? ''}
					/>
				{/each}
			</div>
		{/if}
	</div>

	<div
		class="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent lg:w-20"
	></div>
	<div
		class="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent lg:w-20"
	></div>
</section>

<style>
	@keyframes marquee {
		0% {
			transform: translateX(0);
		}
		100% {
			transform: translateX(-50%);
		}
	}

	.animate-marquee {
		animation: marquee var(--duration) linear infinite;
	}

	.animate-marquee:hover {
		animation-play-state: paused;
	}
</style>
